<template>
  <div class="osk-product-seller">
    <UikitUser
      v-if="seller"
      :user="seller"
      size="m"
      show-follow
      show-date
      hide-type
      @change-follow="handleChange"
      @user-click="handleUserClick"
    />
    <div
      v-if="description"
      class="osk-product-seller__description"
    >
      {{ description }}
    </div>
    <div
      v-if="isInternational"
      class="osk-product-seller__translate-button"
      @click="handleClickTranslate"
    >
      <span class="osk-translate-button__text">
        {{ $t('product.productSeller.translateBtn.showTranslation') }}
      </span>
      <UikitLoader
        v-if="translationPending"
        mini
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useApi } from '~/restAPI';
import { Translate } from '~/restAPI/Translate';

import { ProductDTORes } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const { createApiInstance } = useApi();
const translateApi = createApiInstance(Translate);
const { $addEvent } = useNuxtApp();
const { isInternational } = useInternational();

const props = defineProps<{
  product: ProductDTORes
}>();

const translation = ref<string | null>(null);
const isTranslationShown = ref(false);

const description = computed<string | undefined>(() => {
  if (translation.value && isTranslationShown.value) {
    return translation.value.replaceAll('\r', '');
  }
  return props.product.description?.replaceAll('\r', '');
});
const seller = computed(() => props.product.seller);

const {
  data: translationData,
  execute: getTranslation,
  pending: translationPending,
} = useLazyAsyncData(() => translateApi.getProductTranslate({ productId: props.product.productId })
  .then((res) => res?.data.data), { immediate: false });

translationPending.value = false;

function handleChange(value: boolean) {
  const event = value ? AnalyticsEvents.UserSubscribe : AnalyticsEvents.UserUnsubscribe;

  $addEvent(event, { ...props.product });
}

function handleUserClick() {
  $addEvent(AnalyticsEvents.SellerClick, { ...props.product });
}

async function handleClickTranslate() {
  if (translation.value) {
    isTranslationShown.value = !isTranslationShown.value;
    return;
  }

  await getTranslation();

  translation.value = translationData.value?.description ?? '';

  if (translation.value.length) {
    isTranslationShown.value = true;
  }
}
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.osk-product-seller {
  &__description {
    margin-top: 24px;
    font-size: 14px;
    line-height: 17px;
  }
  &__translate-button {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    margin-top: 12px;
    font-weight: $font-weight-semibold;
    color: $grey;

    .osk-translate-button__text:hover {
      text-decoration: underline;
    }
  }
}
</style>
